import { Popover as BasePopover } from '@base-ui-components/react/popover';
import { cn } from '../cn.js';

/**
 * A surface anchored to a control, for something that needs room but not the whole screen.
 *
 * **Not a Tooltip**, which describes and cannot be interacted with; a popover holds controls and
 * takes focus. **Not a Dialog** either: it does not block the page, so it is wrong for anything
 * that must be answered. A filter panel, a colour choice, a short form — those are popovers.
 */
export const Popover = BasePopover.Root;
export const PopoverTrigger = BasePopover.Trigger;
export const PopoverClose = BasePopover.Close;

export function PopoverContent({
  className,
  children,
  align = 'start',
  sideOffset = 6,
  ...props
}: BasePopover.Popup.Props & { align?: 'start' | 'center' | 'end'; sideOffset?: number }) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner
        align={align}
        sideOffset={sideOffset}
        className="z-(--elevation-popover)"
      >
        <BasePopover.Popup
          className={cn(
            'min-w-(--width-menu) max-w-(--width-panel) rounded-(--radius-md) p-(--space-md)',
            'border border-(--color-border) bg-(--color-surface-elevated) text-(--color-text-primary)',
            'shadow-(--shadow-popover)',
            'transition-opacity duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
            'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            'motion-reduce:transition-none',
            className,
          )}
          {...props}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

export function PopoverTitle({ className, ...props }: BasePopover.Title.Props) {
  return (
    <BasePopover.Title
      className={cn(
        'pb-(--space-sm) text-(length:--font-size-body-large) font-semibold',
        className,
      )}
      {...props}
    />
  );
}
